import type { SupabaseClient } from '@supabase/supabase-js';

import logger from '@/lib/logger';
import type { PersistedTranscriptInput } from '@/lib/transcripts/persistence';
import { isTranscriptNormalizedStorageEnabled } from '@/lib/transcripts/normalizedStorageFeature';

type TranscriptProjectionRow = {
  revision?: number | string | null;
  updated_at?: string | null;
  segment_count?: number | string | null;
  projection_revision?: number | string | null;
  normalized?: boolean | null;
  transcripts?: unknown;
};

type SupabaseErrorLike = {
  code?: string | null;
  message?: string | null;
  details?: string | null;
  hint?: string | null;
};

export interface TranscriptProjection {
  transcripts: PersistedTranscriptInput[];
  revision: number;
  updatedAt: string | null;
  segmentCount: number;
  projectionRevision: number | null;
  normalized: boolean;
  source: 'normalized' | 'legacy';
}

export class TranscriptProjectionReadError extends Error {
  readonly retryable = true;
  readonly status = 503;
  readonly operation: string;
  readonly code?: string | null;
  readonly details?: string | null;
  readonly hint?: string | null;

  constructor(operation: string, message: string, source?: SupabaseErrorLike) {
    super(message);
    this.name = 'TranscriptProjectionReadError';
    this.operation = operation;
    this.code = source?.code;
    this.details = source?.details;
    this.hint = source?.hint;
  }
}

const MISSING_PROJECTION_RPC_CODES = new Set(['PGRST202', '42883']);
let loggedProjectionFallback = false;

function projectionError(
  error: SupabaseErrorLike,
  operation: string,
): TranscriptProjectionReadError {
  return new TranscriptProjectionReadError(
    operation,
    error.message || 'database operation failed',
    error,
  );
}

function isMissingProjectionRpc(error: SupabaseErrorLike | null | undefined): boolean {
  return Boolean(error?.code && MISSING_PROJECTION_RPC_CODES.has(error.code));
}

function nonnegativeInteger(value: unknown, fallback: number): number {
  const numeric = typeof value === 'string' && value.trim() ? Number(value) : value;
  return typeof numeric === 'number'
    && Number.isSafeInteger(numeric)
    && numeric >= 0
    ? numeric
    : fallback;
}

function transcriptArray(
  value: unknown,
  operation: string,
  allowMissing = true,
): PersistedTranscriptInput[] {
  if (value === null || value === undefined) {
    if (allowMissing) return [];
    throw new TranscriptProjectionReadError(
      operation,
      'returned no transcript projection payload',
    );
  }
  if (!Array.isArray(value)) {
    throw new TranscriptProjectionReadError(
      operation,
      'returned a non-array transcript projection',
    );
  }
  if (value.some((item) => !item || typeof item !== 'object' || Array.isArray(item))) {
    throw new TranscriptProjectionReadError(
      operation,
      'returned a malformed transcript segment',
    );
  }
  return value as PersistedTranscriptInput[];
}

function projectionRow(data: unknown): TranscriptProjectionRow | null {
  const candidate = Array.isArray(data) ? data[0] : data;
  return candidate && typeof candidate === 'object' && !Array.isArray(candidate)
    ? candidate as TranscriptProjectionRow
    : null;
}

async function loadLegacyTranscriptProjection(
  supabase: SupabaseClient,
  callId: string,
): Promise<TranscriptProjection> {
  const legacy = await supabase
    .from('transcripts')
    .select('content,revision,updated_at')
    .eq('call_id', callId)
    .maybeSingle();
  if (legacy.error && legacy.error.code !== 'PGRST116') {
    throw projectionError(legacy.error, 'Legacy transcript projection read');
  }

  const transcripts = transcriptArray(legacy.data?.content, 'Legacy transcript projection read');
  const revision = nonnegativeInteger(legacy.data?.revision, 0);
  return {
    transcripts,
    revision,
    updatedAt: typeof legacy.data?.updated_at === 'string' ? legacy.data.updated_at : null,
    segmentCount: transcripts.length,
    projectionRevision: revision,
    normalized: false,
    source: 'legacy',
  };
}

/**
 * Load the complete authoritative transcript projection in immutable ordinal
 * order. During a migration-first/code-first rolling deploy, only an actually
 * missing v3 RPC falls back to the legacy JSONB row. Permission, validation,
 * timeout, and other database failures remain visible instead of being masked.
 */
export async function loadTranscriptProjection(
  supabase: SupabaseClient,
  callId: string,
  options: { normalizedStorageEnabled?: boolean } = {},
): Promise<TranscriptProjection> {
  const normalizedStorageEnabled = options.normalizedStorageEnabled
    ?? isTranscriptNormalizedStorageEnabled();
  if (!normalizedStorageEnabled) {
    return loadLegacyTranscriptProjection(supabase, callId);
  }

  const { data, error } = await supabase.rpc('get_transcript_projection_v3', {
    p_call_id: callId,
  });

  if (!error) {
    const row = projectionRow(data);
    if (!row) {
      throw new TranscriptProjectionReadError(
        'get_transcript_projection_v3',
        'returned no projection row',
      );
    }
    const transcripts = transcriptArray(
      row.transcripts,
      'get_transcript_projection_v3',
      false,
    );
    const segmentCount = nonnegativeInteger(row.segment_count, -1);
    if (segmentCount < 0) {
      throw new TranscriptProjectionReadError(
        'get_transcript_projection_v3',
        'returned a missing or invalid segment count',
      );
    }
    if (segmentCount !== transcripts.length) {
      throw new TranscriptProjectionReadError(
        'get_transcript_projection_v3',
        'returned an inconsistent segment count',
      );
    }
    const revision = nonnegativeInteger(row.revision, -1);
    if (revision < 0 || typeof row.normalized !== 'boolean') {
      throw new TranscriptProjectionReadError(
        'get_transcript_projection_v3',
        'returned missing or invalid projection metadata',
      );
    }
    const projectionRevision = row.projection_revision === null
      || row.projection_revision === undefined
      ? null
      : nonnegativeInteger(row.projection_revision, -1);
    if (projectionRevision !== null && projectionRevision < 0) {
      throw new TranscriptProjectionReadError(
        'get_transcript_projection_v3',
        'returned missing or invalid projection metadata',
      );
    }
    return {
      transcripts,
      revision,
      updatedAt: typeof row.updated_at === 'string' ? row.updated_at : null,
      segmentCount,
      projectionRevision,
      normalized: row.normalized === true,
      source: row.normalized === true ? 'normalized' : 'legacy',
    };
  }

  if (!isMissingProjectionRpc(error)) {
    throw projectionError(error, 'get_transcript_projection_v3');
  }

  if (!loggedProjectionFallback) {
    loggedProjectionFallback = true;
    logger.warn(
      'TranscriptProjection',
      'Normalized transcript projection RPC is not installed; using rolling legacy read compatibility',
      { code: error.code },
    );
  }

  return loadLegacyTranscriptProjection(supabase, callId);
}
